import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { Injectable, inject, untracked } from '@angular/core'
import { apiEndpoint } from '@awork/environments/environment'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import { EventStore } from '@awork/_shared/state/event.store'
import type { TrackingEvent } from '@awork/_shared/services/tracking-service/events'
import type {
  EntityType,
  EntityView,
  IUserActivity,
  IUserTimeTrackingBehavior
} from '@awork/_shared/services/q-event-service/types'

@Injectable({ providedIn: 'root' })
export class QEventService {
  private apiClient = inject(ApiClient)
  private eventStore = inject(EventStore)

  private readonly statsUrl

  constructor() {
    this.statsUrl = `${apiEndpoint}/me/stats`
  }

  /**
   * Makes an API call to register an entity opened event
   * @param {EntityType} entityType
   * @param {string} entityId
   * @returns {Observable<void>}
   */
  trackEntityOpenedEvent(entityType: EntityType, entityId: string): Observable<void> {
    return this.apiClient.post<void>(this.statsUrl, {
      entityId,
      entityType,
      accessedOn: new Date(),
      event: 'Entity Opened'
    })
  }

  /**
   * Returns opened entities, newest first, within a fixed history snapshot.
   * @param {Date} end - End of the history snapshot
   * @param {number} page - One-based page number
   * @param {number} pageSize - Number of events per page
   * @returns {Observable<EntityView[]>} Entity open events
   */
  getEntityViews(end: Date, page: number, pageSize: number): Observable<EntityView[]> {
    const params = new URLSearchParams({
      start: new Date(0).toISOString(),
      end: end.toISOString(),
      page: String(page),
      pageSize: String(pageSize),
      orderBy: 'createdOn desc,id desc'
    })
    return this.apiClient.get<EntityView[]>(`${this.statsUrl}/views?${params}`)
  }

  /**
   * Makes an API call to register an app accessed event
   * @returns {Observable<void>}
   */
  trackAppAccessedEvent(): Observable<void> {
    return this.apiClient.post<void>(this.statsUrl, {
      accessedOn: new Date(),
      event: 'App Accessed'
    })
  }

  /**
   * Returns the first tracked user activity for a given date
   * @param {Date} date
   * @returns {Observable<IUserActivity>}
   */
  getFirstSeenTimestamp(date: Date): Observable<IUserActivity> {
    return this.apiClient.get<IUserActivity>(`${apiEndpoint}/me/stats/workhours?date=${date.toISOString()}`).pipe(
      map(res => {
        if (res.start) {
          // This HTTP callback can arrive while a reactive consumer is active (FRONTEND-WEB-PRF).
          untracked(() => this.eventStore.updateEvent('firstLogin', res.start))
        }

        return res
      })
    )
  }

  /**
   * Makes an API call to track the specified event
   * @param {TrackingEvent} event
   */
  trackEvent(event: TrackingEvent): void {
    this.apiClient
      .post<void>(this.statsUrl, {
        accessedOn: new Date(),
        event
      })
      .subscribe()
  }

  /**
   * Makes an API call to get the time tracking behavior of the user
   * @returns {Observable<boolean>} True if the user uses start stop
   */
  fetchUserTimeTrackingBehavior(): Observable<boolean> {
    return this.apiClient.get<IUserTimeTrackingBehavior>(`${this.statsUrl}/timetracking`).pipe(
      map(timeTrackingBehavior => {
        this.eventStore.updateEvent('usesStartStop', timeTrackingBehavior.usesStartStop)
        return timeTrackingBehavior.usesStartStop
      })
    )
  }
}
